import React from 'react';
import ReactTooltip from 'react-tooltip';
import * as _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import { withSize } from 'react-sizeme';

import ProjectLogo from '../common/ProjectLogo';
import Linkify from 'react-linkify';
import Avatar from '../common/Avatar';
import Modal from '../common/Modal';
import ProjectEdit from './edit/';
import ProjectEditStatus from './edit-status/';
import * as userDuck from '../user/duck';
import * as projectsDuck from '../projects/duck';

class Item extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isMenuOpen: false,
      isInvisibleUsersMenuOpen: false,
      isEditProjectInfoModalOpen: false,
      isEditProjectStatusModalOpen: false,
    };
  }

  toggleMenu = () => {
    this.setState({
      isMenuOpen: !this.state.isMenuOpen,
    });
  };

  toggleInvisibleUsers = () => {
    this.setState({
      isInvisibleUsersMenuOpen: !this.state.isInvisibleUsersMenuOpen,
    });
  };

  getExceedingUserItemsCount = (users) => {
    const userItemWidth = 48; // user item container + its margin
    const containerWidth = this.props.size.width - 2 * 30 - userItemWidth; // exclude paddings

    const isNotBiggerThanContainer =
      users.length * userItemWidth <= containerWidth;
    if (isNotBiggerThanContainer) {
      return 0;
    }

    const gap = users.length * userItemWidth - containerWidth;
    const exceedingUserItemsCount = Math.ceil(gap / userItemWidth);

    return exceedingUserItemsCount;
  };

  getAllUsers = (users, projectId) => {
    const sortedUsersList = _.sortBy(users, [
      (user) =>
        user.projects.find(({ project }) => project === projectId).status,
    ]);

    const usersList = sortedUsersList.map((user) => {
      const isInactive =
        user.projects.find(({ project }) => project === projectId).status ===
        'INACTIVE';

      return (
        <Link
          key={user._id}
          to={`/users/${user._id}`}
          className={isInactive ? 'u-opacity-half' : ''}
        >
          <Avatar
            data-tip={user.name}
            data-for={projectId + user._id}
            className="u-margin-right-mini"
            src={user.getSmallAvatarUrl()}
            width="38"
            height="38"
          />
          <ReactTooltip
            id={projectId + user._id}
            className="c-tooltip"
            effect="float"
          />
        </Link>
      );
    });
    return usersList;
  };

  getVisibleUsers = (users) => {
    const projectId = this.props.project._id;
    const exceedingUserItemsCount = this.getExceedingUserItemsCount(users);

    // If items are less than 2 - no reducing is needed,
    // because when it is only 1 - it will fit,
    // where there arene't any -> no problem.
    const isReducingUserItemsNotNeeded = exceedingUserItemsCount < 2;

    const usersList = this.getAllUsers(users, projectId);
    if (isReducingUserItemsNotNeeded) {
      return usersList;
    }

    return usersList.slice(0, usersList.length - exceedingUserItemsCount);
  };

  getInvisibleUsers = (users) => {
    const projectId = this.props.project._id;
    const exceedingUserItemsCount = this.getExceedingUserItemsCount(users);
    const usersList = this.getAllUsers(users, projectId);
    const additionalUsers = usersList.slice(
      usersList.length - exceedingUserItemsCount
    );

    return additionalUsers;
  };

  renderAdditionalUserCount = (users) => {
    const exceedingUserItemsCount = this.getExceedingUserItemsCount(users);

    // If items are less than 2 - no reducing is needed,
    // because when it is only 1 - it will fit,
    // where there arene't any -> no problem.
    // Therefore - do not display the button!
    const isReducingUserItemsNotNeeded = exceedingUserItemsCount < 2;
    if (isReducingUserItemsNotNeeded) {
      return null;
    }

    return (
      <div className="c-btn--square" onClick={this.toggleInvisibleUsers}>
        {exceedingUserItemsCount}
      </div>
    );
  };

  handleEditPorjectInfoOpen = () => {
    this.setState({ isEditProjectInfoModalOpen: true });
  };

  handleEditPorjectInfoClose = () => {
    this.setState({ isEditProjectInfoModalOpen: false, isMenuOpen: false });
  };

  handleEditProjectStatusOpen = () => {
    this.setState({ isEditProjectStatusModalOpen: true });
  };

  handleEditPorojectStatusClose = () => {
    this.setState({ isEditProjectStatusModalOpen: false, isMenuOpen: false });
  };

  handleProjectUpdateSuccess = () => {
    // Basically update all related entities.
    // Not the best option, but for sure - the fastest one.
    this.props.requestUserDataIfNeeded();

    this.props.requestProjectsDataIfNeeded();

    this.handleEditPorjectInfoClose();
  };

  handleProjectStatusUpdateSuccess = (user) => {
    this.props.updateProjects(user.projects);

    this.props.requestProjectsDataIfNeeded();
    this.handleEditPorjectInfoClose();
  };

  render() {
    const {
      project,
      currentlyLoggedInUserStatus,
      enableEditMode,
      isInactive,
      t,
    } = this.props;
    const {
      isMenuOpen,
      isInvisibleUsersMenuOpen,
      isEditProjectInfoModalOpen,
      isEditProjectStatusModalOpen,
    } = this.state;

    return (
      <div
        className="o-box c-box c-project o-box--large"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          opacity: isInactive ? 0.5 : 1,
        }}
      >
        {enableEditMode && (
          <button
            className="c-btn c-project__btn"
            type="button"
            onClick={this.toggleMenu}
          />
        )}

        {isMenuOpen && (
          <div className="c-project__menu u-margin-top-huge">
            <ul className="c-project-list u-margin-none">
              <li
                onClick={this.handleEditPorjectInfoOpen}
                className="c-project-list__item"
              >
                {t('project.edit.editInfo')}
              </li>
              <li
                onClick={this.handleEditProjectStatusOpen}
                className="c-project-list__item"
              >
                {t('project.edit.editInvolvementStatus')}
              </li>
            </ul>
          </div>
        )}

        <Modal
          isOpen={isEditProjectInfoModalOpen}
          onRequestClose={this.handleEditPorjectInfoClose}
          title={t('project.edit.editInfoTitle')}
        >
          <ProjectEdit
            type={project.type}
            project={project}
            onSuccess={this.handleProjectUpdateSuccess}
          />
        </Modal>

        <Modal
          isOpen={isEditProjectStatusModalOpen}
          onRequestClose={this.handleEditPorojectStatusClose}
          title={t('project.edit.editInvolvementStatusTitle')}
        >
          <ProjectEditStatus
            project={project}
            status={currentlyLoggedInUserStatus}
            onSuccess={this.handleProjectStatusUpdateSuccess}
          />
        </Modal>
        <div className="u-margin-bottom-tiny">
          <Link
            to={`/projects/${project._id}`}
            className={`u-display-flex u-align-items-center ${
              enableEditMode ? 'u-padding-right' : ''
            }`}
          >
            <ProjectLogo src={project.logoUrl} />
            <h4 className="u-margin-bottom-none">{project.name}</h4>
          </Link>
        </div>
        <p style={{ flex: 1 }} className="c-project__text">
          <Linkify
            properties={{
              target: '_blank',
              className: 'u-color-blue-violet',
            }}
          >
            {project.description}
          </Linkify>
        </p>

        <div className="u-display-flex u-flex-row u-flex-wrap">
          <div className="u-display-flex">
            {project.users && this.getVisibleUsers(project.users)}
          </div>
          {this.renderAdditionalUserCount(project.users)}
        </div>

        {isInvisibleUsersMenuOpen && (
          <div
            className="c-box c-avatar__box--primary"
            style={{
              display: 'flex',
              flexDirection: 'row',
              position: 'absolute',
              bottom: '80px',
              right: '30px',
              maxWidth: '200px',
              opacity: isInactive ? 0.5 : 1,
            }}
          >
            <div className="u-display-flex u-flex-row u-flex-wrap">
              {this.getInvisibleUsers(project.users)}
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapDispatchToProps = {
  requestUserDataIfNeeded: userDuck.requestDataIfNeeded,
  updateProjects: userDuck.updateProjects,
  requestProjectsDataIfNeeded: projectsDuck.requestDataIfNeeded,
};

const mapStateToProps = ({ user }, { project, forceInactive }) => {
  const currentProject =
    user.data &&
    user.data.projects.find((item) => item.project._id === project._id);

  const isInactive =
    forceInactive ||
    // Mark project as inactive one
    // if none users actively work on this project
    // but exclude in the count the ones who have removed the project
    // from their profiles.
    project.users.every((user) => {
      return (
        user.projects.find(({ project: pId }) => pId === project._id).status !==
        'ACTIVE'
      );
    });

  return {
    currentlyLoggedInUserStatus: currentProject ? currentProject.status : null,
    isInactive: isInactive,
    enableEditMode: !!currentProject,
  };
};

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  translate(),
  withSize()
)(Item);



// WEBPACK FOOTER //
// ./src/projects/Item.js